// react-native entry for the browser build.
// Re-exports react-native-web and fills in the APIs it does not provide.
import React from "react";
import ReactDOM from "react-dom";
import RNW, {
  Image as WebImage,
  TextInput as WebTextInput,
  Platform as WebPlatform,
  View,
  TouchableOpacity,
  ScrollView,
  BackHandler,
} from "react-native-web";
import Modal from "./Modal";
import Alert from "./Alert";
import emitter from "./emitter";

export * from "react-native-web";

const pick = obj =>
  "web" in obj ? obj.web : "android" in obj ? obj.android : obj.default;

export const Platform = {
  ...WebPlatform,
  OS: "web",
  Version: 28,
  select: pick,
};

export const NativeAppEventEmitter = emitter;
export const DeviceEventEmitter = emitter;
export const BackAndroid = BackHandler;
export const TouchableNativeFeedback = TouchableOpacity;
export const ViewPagerAndroid = ScrollView;
export const DrawerLayoutAndroid = View;
export const requireNativeComponent = () => View;
export const ToastAndroid = {
  SHORT: 2000,
  LONG: 3500,
  show(message) {
    Alert.toast(message);
  },
};

// Android drawable names ({ uri: "logo" }) are served from /drawable
const resolveSource = source => {
  if (
    source &&
    typeof source === "object" &&
    typeof source.uri === "string" &&
    /^[a-z0-9_]+$/i.test(source.uri)
  ) {
    return Object.assign({}, source, { uri: `/drawable/${source.uri}.png?v=devpos3` });
  }
  return source;
};

export const Image = React.forwardRef((props, ref) => (
  <WebImage {...props} ref={ref} source={resolveSource(props.source)} />
));
Image.displayName = "Image";
Object.keys(WebImage).forEach(key => {
  Image[key] = WebImage[key];
});
["getSize", "prefetch", "queryCache", "resizeMode"].forEach(key => {
  if (WebImage[key] !== undefined) {
    Image[key] = WebImage[key];
  }
});

// The app shows formatted values (currency symbols, separators) in numeric
// fields. <input type="number"> rejects those, so a text input with a
// numeric keyboard hint is used instead.
const NUMERIC_KEYBOARDS = ["numeric", "number-pad", "decimal-pad"];

export const TextInput = React.forwardRef((props, ref) => {
  const { keyboardType, ...rest } = props;
  if (!NUMERIC_KEYBOARDS.includes(keyboardType)) {
    return <WebTextInput {...props} ref={ref} />;
  }
  return (
    <WebTextInput
      {...rest}
      ref={instance => {
        if (instance) {
          const node = ReactDOM.findDOMNode(instance);
          if (node && node.setAttribute) {
            node.setAttribute("inputmode", "decimal");
          }
        }
        if (typeof ref === "function") {
          ref(instance);
        } else if (ref) {
          ref.current = instance;
        }
      }}
    />
  );
});
TextInput.displayName = "TextInput";
Object.keys(WebTextInput).forEach(key => {
  TextInput[key] = WebTextInput[key];
});
TextInput.State = WebTextInput.State;

export { Modal, Alert };

export default {
  ...RNW,
  Platform,
  Image,
  TextInput,
  Modal,
  Alert,
  NativeAppEventEmitter,
  DeviceEventEmitter,
  BackAndroid,
  TouchableNativeFeedback,
  ViewPagerAndroid,
  DrawerLayoutAndroid,
  requireNativeComponent,
  ToastAndroid,
};
